import type { ReassessmentMarker } from './reassessment-marker';
import {
  type FrequencySegment,
  normalizeDuration,
  segmentIntervalDays,
  segmentVisitCount,
  segmentsFromPhases,
  totalVisitCount,
} from './session-frequency';
import type { Cadence } from './spec';

/**
 * The default schedule belongs to the phase the patient is IN, never the whole
 * plan: projecting every phase's cadence into one ladder is what produced
 * ENG-5748's 28-visit draft. The generation service and the review tab both
 * resolve through here, so the default cannot differ between them.
 */

export type PhaseCadenceInput = {
  cadence?: Cadence;
  durationDays?: number;
  visitsToComplete?: number;
};

export type PhaseCadence = {
  segments: FrequencySegment[];
  reassessAfterVisits: number;
};

export const FALLBACK_SEGMENT: FrequencySegment = {
  frequency: 1,
  periodUnit: 'wk',
  duration: 8,
  durationUnit: 'wk',
};

export const FALLBACK_REASSESS_VISITS = 4;

/** `setScheduleBodySchema` rejects a reassessment before visit 2, so a resolved
 *  point below it would 422 on the clinician's next save. */
const MIN_REASSESS_VISITS = 2;

/**
 * Stays -1 when every phase is skipped (`durationDays === 0`) rather than
 * flooring to 0 — this must agree with `compileInstanceSpec`'s `firstLiveIndex`,
 * or the seeded cadence and the `in-progress` activity name different phases.
 */
export const currentPhaseIndex = (phases: PhaseCadenceInput[]): number =>
  phases.findIndex((phase) => phase.durationDays !== 0);

/** Only the reassessment point cares where the cadence came from. */
type CadenceSource = 'template' | 'fallback';

// Narrowed to the current phase: an 8-week default inside a 2-week phase is the
// same wrongness as the whole-plan ladder. Too short a phase is not special-cased
// here — `currentPhaseCadence` enforces the reachable-reassessment invariant once,
// on whatever tier resolved.
const invented = (phase: PhaseCadenceInput | undefined): FrequencySegment =>
  phase?.durationDays !== undefined && phase.durationDays > 0
    ? { ...FALLBACK_SEGMENT, ...normalizeDuration(phase.durationDays) }
    : FALLBACK_SEGMENT;

const resolveSegments = (
  authored: FrequencySegment[] | undefined,
  phases: PhaseCadenceInput[],
  index: number,
  phase: PhaseCadenceInput | undefined,
): { segments: FrequencySegment[]; source: CadenceSource } => {
  // One segment per phase means the ladder IS per-phase, so scope it. Mismatched
  // counts were authored against something other than phases — there is no
  // honest index into that, so pass it through untouched.
  if (authored && authored.length > 0) {
    const scoped =
      authored.length === phases.length && index >= 0
        ? authored[index]
        : undefined;
    return { segments: scoped ? [scoped] : authored, source: 'template' };
  }
  const derived = phase ? segmentsFromPhases([phase]) : undefined;
  return derived
    ? { segments: derived, source: 'template' }
    : { segments: [invented(phase)], source: 'fallback' };
};

const resolveReassessAfterVisits = (
  phase: PhaseCadenceInput | undefined,
  segments: FrequencySegment[],
  source: CadenceSource,
): number => {
  const gate = phase?.visitsToComplete;
  if (gate !== undefined && gate >= MIN_REASSESS_VISITS) return gate;

  const projected = segments[0] ? segmentVisitCount(segments[0]) : null;
  // An open-ended cadence gives nothing to reassess against, so take the
  // mid-course default. A cadence too short to reassess in gets the minimum
  // rather than the default — extending a 1-visit phase out to visit 4 would
  // invent three visits the template never asked for.
  if (projected === null) return FALLBACK_REASSESS_VISITS;
  if (projected < MIN_REASSESS_VISITS) return MIN_REASSESS_VISITS;
  // An invented cadence has no phase length worth trusting, so it keeps the
  // mid-course point — capped, or a phase shorter than that never reassesses.
  return source === 'template'
    ? projected
    : Math.min(FALLBACK_REASSESS_VISITS, projected);
};

/**
 * Bounds an open-ended trailing segment so the reassessment is reachable.
 *
 * A clinician states a cadence without stating how long it runs — "once a week,
 * we'll reassess at the fourth" carries no duration. Persisted verbatim that is
 * an unbounded segment, which projects no ladder at all: the card reads "Ongoing
 * / 1×/wks forever", only visit 1 renders, and the reassessment marker points at
 * a visit that does not exist. The stated reassessment IS the duration.
 *
 * Only for cadences we resolve or extract. A clinician who picks "forever" in the
 * editor means it. A nullish marker is a no-op, so callers need no guard.
 */
export function boundedForReassessment(
  segments: FrequencySegment[],
  reassess: ReassessmentMarker | null | undefined,
): FrequencySegment[] {
  const last = segments[segments.length - 1];
  if (!last || !reassess) return segments;
  // A days-based reassessment names no visit to reach, so there is no ladder
  // length to enforce — but an unbounded segment still projects nothing, so it
  // gets the reassessment horizon as its duration.
  if (reassess.afterDays !== undefined) {
    return last.duration !== undefined
      ? segments
      : [
          ...segments.slice(0, -1),
          { ...last, ...normalizeDuration(reassess.afterDays) },
        ];
  }
  // The floor holds even if a caller passes a lower point than the editor allows.
  const target = Math.max(MIN_REASSESS_VISITS, reassess.afterVisits);
  // Null total = an unbounded segment, which projects no ladder at all. A bounded
  // but too-short ladder counts too: a phase gated at one visit leaves no visit
  // for the reassessment to land on.
  const total = totalVisitCount(segments);
  if (total !== null && total >= target) return segments;
  const earlier = segments
    .slice(0, -1)
    .reduce((sum, segment) => sum + (segmentVisitCount(segment) ?? 0), 0);
  return [
    ...segments.slice(0, -1),
    {
      ...last,
      ...normalizeDuration(
        Math.ceil((target - earlier) * segmentIntervalDays(last)),
      ),
    },
  ];
}

/**
 * `authored` is `sessionFrequencyOf(pd)`, passed in rather than read here to keep
 * this a leaf module with no FHIR plumbing — so it also runs over a live
 * CarePlan's phases, not just a PlanDefinition's.
 */
export function currentPhaseCadence(
  authored: FrequencySegment[] | undefined,
  phases: PhaseCadenceInput[],
): PhaseCadence {
  const index = currentPhaseIndex(phases);
  const phase = index >= 0 ? phases[index] : undefined;
  const { segments, source } = resolveSegments(authored, phases, index, phase);
  const reassessAfterVisits = resolveReassessAfterVisits(
    phase,
    segments,
    source,
  );
  // The invariant, enforced once on the result rather than per tier: the ladder
  // must contain the visit the plan says to reassess at. Every tier can violate
  // it — an unbounded extracted cadence, a phase gated at one visit, a phase
  // shorter than the mid-course default.
  return {
    segments: boundedForReassessment(segments, {
      afterVisits: reassessAfterVisits,
      bookedManually: false,
    }),
    reassessAfterVisits,
  };
}
